import { Locale } from 'vue-i18n';
import { AddLinkMenus } from '@/appsync/graphql/link';
import { graphqlQuery } from '@/appsync/service/GraphqlUtils';
import {
  fetchLinkMenus,
  LinkMenuLanguage,
} from '@/appsync/service/PortalLinkMenuQueryService';
import { ID, LinkMenu, TenantLandscape } from '@/types/type';

interface InsertTarget {
  menuId?: string;
  url: string;
  title: string;
  sort: number;
}

interface UpdateTarget {
  menuId: string;
  url: string;
  title: string;
  sort: number;
}

export interface UpdateLinkMenusParam extends TenantLandscape {
  menus: LinkMenu[];
}

export const updateLinkMenus = async (param: UpdateLinkMenusParam) => {
  const menus: LinkMenu[] = await fetchLinkMenus(param);
  const menuIds = menus.map(menu => menu.id);
  const insertTargetMenus: InsertTarget[] = [];
  const updateTargetMenus: UpdateTarget[] = [];
  const deleteTargetMenuIds: ID[] = [...menuIds];
  param.menus.forEach((menu: LinkMenu, sort: number) => {
    if (menu.id.length === 0) {
      insertTargetMenus.push({ ...menu, sort });
    } else if (menuIds.includes(menu.id)) {
      updateTargetMenus.push({ ...menu, menuId: menu.id, sort });
      deleteTargetMenuIds.splice(deleteTargetMenuIds.indexOf(menu.id), 1);
    }
  });
  if (insertTargetMenus.length > 0) {
    await insertLinkMenus(param, insertTargetMenus);
  }
  if (updateTargetMenus.length > 0) {
    await updateTargetLinkMenus(param, updateTargetMenus);
  }
  if (deleteTargetMenuIds.length > 0) {
    await deleteTargetLinkMenus(param, deleteTargetMenuIds);
  }
};

const insertLinkMenus = async (
  param: UpdateLinkMenusParam,
  insertTargetMenus: InsertTarget[]
): Promise<void> => {
  const addParam: AddLinkMenusQueryParam = {
    ...param,
    contents: insertTargetMenus,
  };
  const result = await graphqlQuery<AddLinkMenus, {}>(addLinkMenus(addParam));

  if (result) {
    Object.values(result).forEach(content => {
      insertTargetMenus.forEach((target: InsertTarget) => {
        if (content && content.sort === target.sort && content.id) {
          target.menuId = content.id;
        }
      });
    });
  }

  const details: LinkMenuNameDetail[] = [];
  insertTargetMenus.forEach((target: InsertTarget) => {
    if (target.menuId) {
      details.push({
        menuId: target.menuId,
        language: LinkMenuLanguage,
        description: target.title,
      });
    }
  });
  const addDescriptionParam: AddLinkMenusDescriptionQueryParam = {
    ...param,
    details,
  };
  await graphqlQuery(addLinkMenuDescriptions(addDescriptionParam));
};

const updateTargetLinkMenus = async (
  param: UpdateLinkMenusParam,
  updateTargetMenus: UpdateTarget[]
): Promise<void> => {
  const updateParam: UpdateLinkMenusQueryParam = {
    ...param,
    contents: updateTargetMenus,
  };
  await graphqlQuery(updateLinkMenusMutation(updateParam));

  const details: UpdateLinkMenuNameDetail[] = updateTargetMenus.map(
    (target: UpdateTarget) => {
      return { ...target, language: LinkMenuLanguage };
    }
  );
  const updateDescriptionParam: UpdateLinkMenusNameQueryParam = {
    ...param,
    details,
  };
  await graphqlQuery(updateLinkMenuDescriptions(updateDescriptionParam));
};

const deleteTargetLinkMenus = async (
  param: UpdateLinkMenusParam,
  deleteTargetMenuIds: ID[]
): Promise<void> => {
  const deleteLinkMenusQueryKey: DeleteLinkMenusQueryKey = {
    ...param,
    languages: [LinkMenuLanguage],
    menuIds: deleteTargetMenuIds,
  };
  await graphqlQuery(deleteLinkMenus(deleteLinkMenusQueryKey));
  await graphqlQuery(deleteAllLinkMenuDescriptions(deleteLinkMenusQueryKey));
};

interface AddLinkMenusHeader {
  url: string;
  sort: number;
}

export interface AddLinkMenusQueryParam extends TenantLandscape {
  contents: AddLinkMenusHeader[];
}

export const addLinkMenus = (param: AddLinkMenusQueryParam) => {
  const getAddLinkMenusContent = (
    tenant: string,
    landscape: string,
    content: AddLinkMenusHeader,
    index: number
  ): string => {
    return `
        result${index}: createLink(input: {
          tenant: "${tenant}",
          landscape: "${landscape}",
          url:"${content.url}",
          sort:${content.sort}
        }) {
          id,
          url,
          sort
        }
      `;
  };
  const content: string = param.contents
    .map((content: AddLinkMenusHeader, index: number) =>
      getAddLinkMenusContent(param.tenant, param.landscape, content, index)
    )
    .join('');
  const result = `
      mutation CreateLink{
        ${content}
      }
    `;
  return result;
};

export interface LinkMenuNameDetail {
  menuId: ID;
  language: Locale;
  description: string;
}

export interface AddLinkMenusDescriptionQueryParam extends TenantLandscape {
  details: LinkMenuNameDetail[];
}

export const addLinkMenuDescriptions = (
  param: AddLinkMenusDescriptionQueryParam
) => {
  const getAddLinkMenuDescriptionsContent = (
    tenant: string,
    landscape: string,
    detail: LinkMenuNameDetail,
    index: number
  ): string => {
    return `
        result${index}: createLinkMenuDescription(input: {
          tenant: "${tenant}",
          landscape: "${landscape}",
          menuId:"${detail.menuId}",
          language:${detail.language},
          description:"${detail.description}"
        }) {
          menuId
        }
      `;
  };
  const content: string = param.details
    .map((detail: LinkMenuNameDetail, index: number) =>
      getAddLinkMenuDescriptionsContent(
        param.tenant,
        param.landscape,
        detail,
        index
      )
    )
    .join('');
  return `
      mutation CreateLinkMenuDescription{
        ${content}
      }
    `;
};

interface UpdateLinkMenusHeader {
  menuId: ID;
  url: string;
  sort: number;
}

export interface UpdateLinkMenusQueryParam extends TenantLandscape {
  contents: UpdateLinkMenusHeader[];
}

export const updateLinkMenusMutation = (param: UpdateLinkMenusQueryParam) => {
  const getUpdateLinkMenusContent = (
    tenant: string,
    landscape: string,
    content: UpdateLinkMenusHeader,
    index: number
  ): string => {
    return `
        result${index}: updateLink(input: {
          id: "${content.menuId}"
          tenant: "${tenant}",
          landscape: "${landscape}",
          url:"${content.url}",
          sort:${content.sort}
        }) {
          id,
          url,
          sort
        }
      `;
  };
  const content: string = param.contents
    .map((content: UpdateLinkMenusHeader, index: number) =>
      getUpdateLinkMenusContent(param.tenant, param.landscape, content, index)
    )
    .join('');
  return `
      mutation UpdateLink{
        ${content}
      }
    `;
};

export interface UpdateLinkMenuNameDetail {
  menuId: ID;
  language: Locale;
  title: string;
}

export interface UpdateLinkMenusNameQueryParam extends TenantLandscape {
  details: UpdateLinkMenuNameDetail[];
}

export const updateLinkMenuDescriptions = (
  param: UpdateLinkMenusNameQueryParam
) => {
  const getUpdateLinkMenuDescriptionsContent = (
    tenant: string,
    landscape: string,
    detail: UpdateLinkMenuNameDetail,
    index: number
  ): string => {
    return `
        result${index}: updateLinkMenuDescription(input: {
          tenant: "${tenant}",
          landscape: "${landscape}",
          menuId:"${detail.menuId}",
          language:${detail.language},
          description:"${detail.title}"
        }) {
          menuId
        }
      `;
  };
  const content: string = param.details
    .map((detail: UpdateLinkMenuNameDetail, index: number) =>
      getUpdateLinkMenuDescriptionsContent(
        param.tenant,
        param.landscape,
        detail,
        index
      )
    )
    .join('');
  return `
      mutation UpdateLinkMenuDescription{
        ${content}
      }
    `;
};

export interface DeleteLinkMenusQueryKey extends TenantLandscape {
  languages: Locale[];
  menuIds: string[];
}

export const deleteLinkMenus = (key: DeleteLinkMenusQueryKey) => {
  const getDeleteLinkMenusContent = (
    tenant: string,
    landscape: string,
    menuId: ID,
    index: number
  ): string => {
    return `
        result${index}: deleteLink(input: {
          id: "${menuId}",
        },
        condition: {
          tenant: {eq: "${tenant}"},
          landscape: {eq: "${landscape}"}
        }) {
          id
        }
      `;
  };
  const content: string = key.menuIds
    .map((menuId: ID, index: number) =>
      getDeleteLinkMenusContent(key.tenant, key.landscape, menuId, index)
    )
    .join('');
  return `
      mutation DeleteLink{
        ${content}
      }
    `;
};

export const deleteAllLinkMenuDescriptions = (key: DeleteLinkMenusQueryKey) => {
  const getDeleteLinkMenuDescriptionsContent = (
    tenant: string,
    landscape: string,
    language: string,
    menuId: ID,
    index: number
  ): string => {
    return `
        result${index}: deleteLinkMenuDescription(input: {
          tenant: "${tenant}",
          landscape: "${landscape}",
          menuId:"${menuId}",
          language:${language},
        }) {
          menuId
        }
      `;
  };
  const queries: string[] = [];
  let index = 0;
  for (let menuId of key.menuIds) {
    for (let language of key.languages) {
      queries.push(
        getDeleteLinkMenuDescriptionsContent(
          key.tenant,
          key.landscape,
          language,
          menuId,
          index++
        )
      );
    }
  }
  return `
      mutation DeleteLinkMenuDescription{
        ${queries.join('')}
      }
    `;
};
